import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../state/authStore";
import { AuthLayout, Field, FormError, PrimaryButton, inputClass } from "./AuthLayout";
import { PasswordInput } from "./PasswordInput";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);
  const pending = useAuthStore((s) => s.pending);
  const errorMessage = useAuthStore((s) => s.errorMessage);
  const status = useAuthStore((s) => s.status);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const searchParams = new URLSearchParams(location.search);
  const next = searchParams.get("next") || "/";
  const justDeleted = searchParams.get("deleted") === "1";

  if (status === "authed") {
    navigate(next, { replace: true });
    return null;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(next, { replace: true });
    } catch {
      /* error message is in the store */
    }
  }

  return (
    <AuthLayout title="Anmelden" subtitle="Mit deiner E-Mail und deinem Passwort.">
      {justDeleted && (
        <div
          data-testid="deleted-toast"
          role="status"
          className="mb-4 rounded-xl border border-success/40 bg-success-soft px-4 py-3 text-sm text-fg-soft"
        >
          Konto gelöscht. Eine Anmeldung mit dieser E-Mail ist nicht mehr möglich. Du kannst dich aber jederzeit mit einem neuen Einladungscode neu registrieren.
        </div>
      )}
      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <Field label="E-Mail" htmlFor="email">
          <input
            id="email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={pending}
            className={inputClass}
            data-testid="email-input"
          />
        </Field>
        <Field label="Passwort" htmlFor="password">
          <PasswordInput
            id="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            required
            disabled={pending}
          />
        </Field>
        {errorMessage && <FormError message={errorMessage} />}
        <PrimaryButton pending={pending} testid="submit-login">
          Einloggen
        </PrimaryButton>
        <div className="flex items-center justify-between text-sm pt-1">
          <Link to="/forgot" className="text-accent hover:underline">
            Passwort vergessen?
          </Link>
          <Link to="/register" className="text-accent hover:underline">
            Konto anlegen
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
